import React from 'react';
import Link from 'components/link';
import Button from 'components/control/button';
import MapIcon from 'components/icon/map-icon';
import { AnalyticsController } from 'analytics/analytics-controller';
import { buildClassName } from 'utils/build-class-name';
import zoocasaForSaleStyles from '../../themed-for-sale-section/zoocasa/style.module.scss';
import lightForSaleStyles from './zoocasa-light-for-sale-section.module.scss';
import { useUserContext } from 'contexts';
import { homepageIds } from 'constants/test-constants';
import {
  GTM_CLICK_HOME_PAGE_LINKED_TEXT_BUTTON,
  GTM_CLICK_HOME_PAGE_FOR_SALE_VIEW_MAP_BUTTON,
} from 'constants/events';
import replaceFrenchAccents from 'utils/replace-french-accents';
import dynamic from 'next/dynamic';
import { useIsTablet } from 'hooks/use-size-class';

import type Listing from 'data/listing';
import type { ListingCardData } from 'components/listing-card/listing-card';
import type { OnListingCardClickProps } from 'utils/listing-card';

const ListingCardCarousel = dynamic(() => import('components/listing-card-carousel'));

export type ZoocasaLightForSaleSectionProps = {
  className?: string;
  titleClassName?: string;
  isCrawler: boolean;
  listings: ListingCardData[];
  homesForSaleUrl: string;
  mapViewUrl: string;
  onListingCardClick: (props: OnListingCardClickProps) => void;
  /** Translated title line **/
  sectionTitle: React.ReactNode;
  citySelector: React.ReactNode;
};

/**
 * Zoocasa Light homepage for-sale section. New Popular City Dropdown, allowing users to explore listings and select a popular city from Canada/US. 
 **/
export default function ZoocasaLightForSaleSection({
  className,
  titleClassName,
  isCrawler,
  listings,
  homesForSaleUrl,
  mapViewUrl,
  onListingCardClick,
  sectionTitle,
  citySelector,
}: ZoocasaLightForSaleSectionProps) {
  const { userLocation } = useUserContext();
  const isTablet = useIsTablet();


  return (
    <section
      className={buildClassName(zoocasaForSaleStyles.component, lightForSaleStyles.sectionLight, className)}
    >
      <div className={zoocasaForSaleStyles['home-block']} data-testid={homepageIds.homesForSaleWidget}>
        <div className={buildClassName(zoocasaForSaleStyles.header, lightForSaleStyles.headerLight)}>
          <div
            className={buildClassName(zoocasaForSaleStyles.left, titleClassName)}
            data-testid={homepageIds.homesForSaleTitle}
          >
            <Link
              href={replaceFrenchAccents(homesForSaleUrl)}
              onClick={() => AnalyticsController.trackEvent(GTM_CLICK_HOME_PAGE_LINKED_TEXT_BUTTON)}
              className={zoocasaForSaleStyles['section-title-link']}
            >
              <p className={zoocasaForSaleStyles.properties}>{sectionTitle}</p>
            </Link>

            <div
              className={zoocasaForSaleStyles['location-selector-wrap']}
            >
              {citySelector}
            </div>
          </div>
          {!isTablet && (
            <Link href={mapViewUrl}>
              <Button
                label="Map View"
                Icon={MapIcon}
                theme="secondary"
                testId={homepageIds.homesForSaleMapViewButton}
                onClick={() => {
                  AnalyticsController.trackEvent(GTM_CLICK_HOME_PAGE_FOR_SALE_VIEW_MAP_BUTTON);
                }}
              />
            </Link>
          )}
        </div>
        <ListingCardCarousel
          key={homesForSaleUrl}
          listingCardsData={{
            listings: listings as Listing[],
            ctaType: 'available',
            userLocation,
            redirectUrl: replaceFrenchAccents(homesForSaleUrl),
          }}
          isCrawler={isCrawler}
          slidesPerGroupAuto
          onListingCardClick={onListingCardClick}
        />
        {isTablet && (
          <div className={zoocasaForSaleStyles['mobile-view-map']}>
            <Link href={mapViewUrl}>
              <Button
                label="Map View"
                Icon={MapIcon}
                theme="secondary"
                testId={homepageIds.homesForSaleMapViewButton}
                onClick={() => {
                  AnalyticsController.trackEvent(GTM_CLICK_HOME_PAGE_FOR_SALE_VIEW_MAP_BUTTON);
                }}
              />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
